class FantaserPolling {
    constructor() {
        this.taskId = null;
        this.pollingInterval = null;
        this.isPolling = false;
        this.pollCount = 0;
        this.maxPolls = 120; // Max 20 minutes (120 * 10 seconds)
        this.init();
    }

    init() {
        // Check for task ID in localStorage
        const storedTaskId = localStorage.getItem('fantaser_task_id');
        const storedTimestamp = localStorage.getItem('fantaser_task_timestamp');
        
        // Clear old tasks (older than 30 minutes)
        if (storedTimestamp) {
            const age = Date.now() - parseInt(storedTimestamp);
            if (age > 30 * 60 * 1000) {
                this.clearStorage();
                return;
            }
        }
        
        if (storedTaskId) {
            this.taskId = storedTaskId;
            // Only start polling if we're on an article edit page
            if (this.isOnArticleEditPage()) {
                this.startPolling();
            }
        }

        // Listen for custom events
        document.addEventListener('fantaser:start', (event) => {
            this.taskId = event.detail.taskId;
            localStorage.setItem('fantaser_task_id', this.taskId);
            localStorage.setItem('fantaser_task_timestamp', Date.now().toString());
            this.startPolling();
        });

        document.addEventListener('fantaser:stop', () => {
            this.stopPolling();
            this.clearStorage();
        });
        
        // Stop polling when leaving the page
        window.addEventListener('beforeunload', () => {
            this.stopPolling();
        });
    }
    
    isOnArticleEditPage() {
        return window.location.pathname.includes('/articles/') && 
               (window.location.pathname.includes('/edit') || window.location.pathname.match(/\/articles\/\d+$/));
    }
    
    clearStorage() {
        localStorage.removeItem('fantaser_task_id');
        localStorage.removeItem('fantaser_task_timestamp');
    }

    startPolling() {
        if (this.isPolling || !this.taskId) return;

        this.isPolling = true;
        this.pollCount = 0;
        this.showLoadingIndicator();

        this.pollingInterval = setInterval(() => {
            this.checkTaskStatus();
        }, 10000); // Poll every 10 seconds

        // Initial check
        this.checkTaskStatus();
    }

    stopPolling() {
        this.isPolling = false;
        if (this.pollingInterval) {
            clearInterval(this.pollingInterval);
            this.pollingInterval = null;
        }
        this.hideLoadingIndicator();
    }

    async checkTaskStatus() {
        if (!this.taskId) {
            this.stopPolling();
            return;
        }
        
        // Stop after max polls
        this.pollCount++;
        if (this.pollCount >= this.maxPolls) {
            console.log('FantaserPolling: Max polls reached, stopping');
            this.stopPolling();
            this.clearStorage();
            this.showNotification('Превышено время ожидания генерации', 'error');
            return;
        }

        try {
            const response = await fetch(`/admin/fantaser/status/${this.taskId}`);
            const data = await response.json();

            if (data.status === 'completed' || data.should_clear_storage) {
                this.handleCompleted(data);
            } else if (data.status === 'failed') {
                this.handleFailed(data);
            }
            // For pending/processing, just continue polling
        } catch (error) {
            console.error('Error checking Fantaser status:', error);
        }
    }

    handleCompleted(data) {
        this.stopPolling();
        this.clearStorage();
        
        // Show success notification
        this.showNotification('Статья успешно сгенерирована!', 'success');
        
        // Update form fields if we're on edit page
        this.updateFormFields(data);
        
        // Dispatch completion event
        document.dispatchEvent(new CustomEvent('fantaser:completed', {
            detail: data
        }));
        
        // Reload page after short delay
        setTimeout(() => window.location.reload(), 1500);
    }

    handleFailed(data) {
        this.stopPolling();
        this.clearStorage();
        
        // Show error notification
        this.showNotification('Ошибка генерации: ' + (data.error || data.message || 'Неизвестная ошибка'), 'error');
        
        // Dispatch failure event
        document.dispatchEvent(new CustomEvent('fantaser:failed', {
            detail: data
        }));
    }

    updateFormFields(data) {
        // Update title field
        const titleField = document.querySelector('input[name="title"]');
        if (titleField && data.title) {
            titleField.value = data.title;
            titleField.dispatchEvent(new Event('input', { bubbles: true }));
        }

        // Update meta title field
        const metaTitleField = document.querySelector('input[name="meta_title"]');
        if (metaTitleField && (data.meta_title || data.title)) {
            metaTitleField.value = data.meta_title || data.title;
            metaTitleField.dispatchEvent(new Event('input', { bubbles: true }));
        }

        // Update meta description field
        const metaDescField = document.querySelector('textarea[name="meta_description"]');
        if (metaDescField && (data.meta_description || data.description)) {
            metaDescField.value = data.meta_description || data.description;
            metaDescField.dispatchEvent(new Event('input', { bubbles: true }));
        }

        // Update content field (for TinyEditor)
        const contentField = document.querySelector('.tox-tinymce');
        if (contentField && data.text) {
            // Try to update TinyEditor content
            const editor = window.tinymce?.get('content');
            if (editor) {
                editor.setContent(data.text);
            }
        }

        // Update doctors field
        if (data.doctors && Array.isArray(data.doctors)) {
            // This is more complex as it involves the repeater component
            // For now, we'll just show a notification that doctors were added
            this.showNotification('Врачи добавлены в статью', 'info');
        }
    }

    showLoadingIndicator() {
        // Remove existing indicator first
        this.hideLoadingIndicator();
        
        const indicator = document.createElement('div');
        indicator.id = 'fantaser-loading';
        indicator.className = 'fixed top-4 right-4 bg-blue-500 text-white px-4 py-2 rounded-lg shadow-lg z-50 flex items-center space-x-2';
        indicator.innerHTML = `
            <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            <span>Статья генерируется...</span>
        `;
        document.body.appendChild(indicator);
    }

    hideLoadingIndicator() {
        const indicator = document.getElementById('fantaser-loading');
        if (indicator) {
            indicator.remove();
        }
    }

    showNotification(message, type = 'info') {
        // Use Filament's notification system if available
        if (window.Filament && window.Filament.notification) {
            window.Filament.notification()
                .title(message)
                .success(type === 'success')
                .danger(type === 'error')
                .info(type === 'info')
                .send();
        } else {
            // Fallback to browser notification
            const notification = document.createElement('div');
            notification.className = `fixed top-4 right-4 px-4 py-2 rounded-lg shadow-lg z-50 ${
                type === 'success' ? 'bg-green-500' : 
                type === 'error' ? 'bg-red-500' : 
                'bg-blue-500'
            } text-white`;
            notification.textContent = message;
            document.body.appendChild(notification);
            
            setTimeout(() => {
                notification.remove();
            }, 5000);
        }
    }

    addPollingIndicator() {
        // Add a small indicator to the edit page showing polling status
        const header = document.querySelector('.filament-page-header');
        if (header && this.taskId) {
            const indicator = document.createElement('div');
            indicator.id = 'fantaser-edit-indicator';
            indicator.className = 'text-sm text-blue-600 mt-2';
            indicator.innerHTML = '🔄 Статья генерируется в фоновом режиме...';
            header.appendChild(indicator);
        }
    }
}

// Initialize the polling system
document.addEventListener('DOMContentLoaded', () => {
    window.fantaserPolling = new FantaserPolling();
});

// Export for manual usage
window.FantaserPolling = FantaserPolling;
